import type { FormatDateOptions } from 'react-intl';

import type { DateSerializable } from '@vs-shop/schemas/DateSerializable';
import type { DateTimeSerializable } from '@vs-shop/schemas/DateTimeSerializable';
import { BigDecimal } from 'effect';

import { getCurrencyFormatConfig } from '../../../config/currency';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../utils/intl';

import { decodeUnknownPriceWithCurrencySync } from '@vs-shop/schemas/primitive/priceWithCurrency';

// Pure, string-returning formatters that live OUTSIDE a `use client` module so
// they are callable from both server and client components. `Intl.tsx` carries a
// `use client` directive (its `react-intl` element exports need it)

export type MoneyT = {
  readonly amount: number;
  readonly currency: string;
};

export type MoneyProps = {
  readonly value: MoneyT;
} & { fractionDigits?: number };

/**
 * Returns a plain string (not a React element) so it can be used outside a `react-intl` provider
 * Example ---> intlFormatDate({ _tag: 'DateTime', value: '2026-01-04T13:29:46.625Z' })
 */
export function intlFormatDate(
  value: DateSerializable | DateTimeSerializable,
  options?: FormatDateOptions
): string {
  const intl = getMemoizedIntlShapeWithDefaultLocale();
  return intl.formatDate(value.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  });
}

/**
 * Returns a plain string (not a React element) so it can be used outside a `react-intl` provider
 * Example ---> intlFormatMoney({ value: { amount: 359000, currency: 'CZK' } })
 */
export function intlFormatMoney({ value }: MoneyProps): string {
  const priceWithCurrency = decodeUnknownPriceWithCurrencySync({
    price: value.amount,
    currency: value.currency,
  });

  const intl = getMemoizedIntlShapeWithDefaultLocale();
  const config = getCurrencyFormatConfig(priceWithCurrency.currency);

  return intl.formatNumber(BigDecimal.toNumberUnsafe(priceWithCurrency.price), {
    style: 'currency',
    currency: priceWithCurrency.currency,
    minimumFractionDigits: config.decimals,
    maximumFractionDigits: config.decimals,
  });
}
